// Barra inferior de preview de voz, estilo ElevenLabs: avatar + nome, seletor de
// idioma do preview, voltar/avançar 10s, play/pause, barra de progresso com tempo,
// download e fechar. Lê o player COMPARTILHADO (VoicePreviewContext) — aparece
// quando uma voz toca, tanto na página de TTS quanto dentro do modal de vozes.
import React, { useEffect, useRef, useState } from 'react';
import { Play, Pause, X, Download, RotateCcw, RotateCw, ChevronUp, Check } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useVoicePreview, previewLanguagesOf } from '../../contexts/VoicePreviewContext';
import { getFlagCode, getPrimaryFlagCode, LANGUAGE_PT, ACCENT_PT, translate } from '../../utils/voiceMetadata';
import { useSidebar } from '../../contexts/SidebarContext';
import FlagIcon from './FlagIcon';

const fmt = (s) => {
  s = Math.max(0, Math.floor(Number(s) || 0));
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return `${m}:${String(sec).padStart(2, '0')}`;
};
const keyOf = (e) => `${e.language || ''}|${e.accent || ''}`;
const langLabel = (e, t) => {
  const lang = translate(e.language, LANGUAGE_PT);
  const acc = e.accent && e.accent.toLowerCase() !== 'standard' ? ` (${translate(e.accent, ACCENT_PT)})` : '';
  return `${t('audio.preview_of', 'Pré-visualização de')} ${lang}${acc}`;
};

export default function VoicePreviewBar() {
  const { t } = useTranslation();
  const vp = useVoicePreview();
  const { sidebarWidth, isMobile } = useSidebar();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => { setMenuOpen(false); }, [vp.barVoice && vp.barVoice.id, vp.barKind]);
  useEffect(() => {
    if (!menuOpen) return undefined;
    const onDoc = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [menuOpen]);

  if (!vp.barActive) return null;

  const isResult = vp.barKind === 'result';
  const name = vp.barName || '—';
  const entries = isResult ? [] : previewLanguagesOf(vp.barVoice);
  const current = entries.find((e) => keyOf(e) === vp.barLangKey) || null;
  const flag = isResult ? null : (current ? getFlagCode(current.language, current.accent, current.locale) : getPrimaryFlagCode(vp.barVoice));
  const dur = vp.barDuration || 0;
  const z = 2147483000; // acima de tudo (inclusive o modal de vozes)

  return (
    <div
      style={{
        position: 'fixed', left: isMobile ? 0 : sidebarWidth, right: 0, bottom: 0, zIndex: z,
        background: 'var(--dp-bg-card)', borderTop: '1px solid var(--dp-border-subtle)',
        boxShadow: '0 -8px 28px rgba(0,0,0,0.35)', fontFamily: 'var(--dp-font)',
        padding: '8px max(16px, env(safe-area-inset-left)) calc(8px + env(safe-area-inset-bottom)) 16px',
        display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap',
        animation: 'dp-pageFadeIn 0.2s ease',
      }}
      role="region"
      aria-label={t('audio.now_playing', 'Tocando agora')}
    >
      {/* Esquerda: avatar + nome + seletor de idioma */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: '1 1 240px', minWidth: 0 }}>
        <div style={{ width: 30, height: 30, borderRadius: '50%', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(135deg, rgba(13,174,255,0.25), rgba(6,182,212,0.18))', color: 'var(--dp-blue-500)', fontWeight: 800, fontSize: 13, overflow: 'hidden' }}>
          {(name || '?').trim().charAt(0).toUpperCase()}
        </div>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--dp-text-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 220 }}>{name}</div>
          {entries.length > 0 && (
            <div style={{ position: 'relative' }} ref={menuRef}>
              <button
                onClick={() => entries.length > 1 && setMenuOpen((o) => !o)}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 5, marginTop: 1, background: 'transparent', border: 'none', padding: 0, cursor: entries.length > 1 ? 'pointer' : 'default', color: 'var(--dp-text-secondary)', fontFamily: 'var(--dp-font)', fontSize: 11.5 }}
              >
                <FlagIcon code={flag} size="13px" />
                <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 200 }}>{current ? langLabel(current, t) : ''}</span>
                {entries.length > 1 && <ChevronUp size={13} style={{ transform: menuOpen ? 'none' : 'rotate(180deg)', transition: 'transform 0.15s' }} />}
              </button>
              {menuOpen && entries.length > 1 && (
                <div style={{ position: 'absolute', bottom: 'calc(100% + 8px)', left: 0, zIndex: z + 1, minWidth: 230, maxHeight: 280, overflowY: 'auto', background: 'var(--dp-bg-card)', border: '1px solid var(--dp-border-subtle)', borderRadius: 10, boxShadow: '0 12px 32px rgba(0,0,0,0.5)', padding: 4 }}>
                  {entries.map((e, i) => {
                    const k = keyOf(e);
                    const isCur = k === vp.barLangKey;
                    return (
                      <button
                        key={`${k}|${i}`}
                        onClick={() => { setMenuOpen(false); vp.setLanguage(e); }}
                        style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', background: isCur ? 'rgba(13,174,255,0.12)' : 'transparent', border: 'none', borderRadius: 7, cursor: 'pointer', color: isCur ? 'var(--dp-blue-500)' : 'var(--dp-text-primary)', fontFamily: 'var(--dp-font)', fontSize: 12.5, textAlign: 'left' }}
                      >
                        <span style={{ width: 13, display: 'inline-flex' }}>{isCur && <Check size={12} />}</span>
                        <FlagIcon code={getFlagCode(e.language, e.accent, e.locale)} size="14px" />
                        <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{langLabel(e, t)}</span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Centro: voltar 10s · play/pause · avançar 10s */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
        <button onClick={() => vp.barSkip(-10)} title="-10s" style={ICON_BTN}><RotateCcw size={18} /></button>
        <button
          onClick={vp.barToggle}
          title={vp.barIsPlaying ? t('audio.pause', 'Pausar') : t('audio.play', 'Reproduzir')}
          style={{ width: 40, height: 40, borderRadius: '50%', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--dp-text-primary)', color: 'var(--dp-bg-card)', flexShrink: 0 }}
        >
          {vp.barIsPlaying ? <Pause size={18} fill="currentColor" /> : <Play size={18} fill="currentColor" style={{ marginLeft: 2 }} />}
        </button>
        <button onClick={() => vp.barSkip(10)} title="+10s" style={ICON_BTN}><RotateCw size={18} /></button>
      </div>

      {/* Progresso com tempos */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: '2 1 260px', minWidth: 160 }}>
        <span style={{ fontSize: 11, color: 'var(--dp-text-secondary)', fontVariantNumeric: 'tabular-nums', width: 34, textAlign: 'right' }}>{fmt(vp.barCurrentTime)}</span>
        <input
          type="range" min={0} max={dur || 0} step={0.1} value={Math.min(vp.barCurrentTime, dur || 0)}
          onChange={(e) => vp.barSeek(parseFloat(e.target.value))}
          aria-label={t('audio.seek', 'Avançar/voltar')}
          style={{ flex: 1, accentColor: 'var(--dp-text-primary)', cursor: 'pointer', height: 4 }}
        />
        <span style={{ fontSize: 11, color: 'var(--dp-text-secondary)', fontVariantNumeric: 'tabular-nums', width: 34 }}>{fmt(dur)}</span>
      </div>

      {/* Direita: download + fechar */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
        {vp.barCurrentUrl && (
          <a href={vp.barCurrentUrl} download target="_blank" rel="noreferrer" title={t('audio.download', 'Baixar')} style={{ ...ICON_BTN, textDecoration: 'none' }}>
            <Download size={17} />
          </a>
        )}
        <button onClick={vp.barStop} title={t('audio.close', 'Fechar')} style={ICON_BTN}><X size={18} /></button>
      </div>
    </div>
  );
}

const ICON_BTN = {
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  width: 34, height: 34, borderRadius: 8, background: 'transparent',
  border: 'none', cursor: 'pointer', color: 'var(--dp-text-secondary)',
};
